import { useState, useEffect, useCallback } from 'react';
import { chatAPI } from '../services/api';

interface AsyncChatState {
  requestId: string | null;
  status: 'idle' | 'processing' | 'completed' | 'failed';
  result: string | null;
  error: string | null;
  isPolling: boolean;
}

export const useAsyncChat = () => {
  const [state, setState] = useState<AsyncChatState>({
    requestId: null,
    status: 'idle',
    result: null,
    error: null,
    isPolling: false
  });

  const startAsyncRequest = useCallback(async (message: string, conversationId?: number) => {
    try {
      setState(prev => ({
        ...prev,
        status: 'processing',
        error: null,
        result: null
      }));

      const response = await chatAPI.startAsyncChat({ message, conversationId });
      
      setState(prev => ({
        ...prev,
        requestId: response.requestId,
        status: 'processing',
        isPolling: true
      }));

      return response.requestId;
    } catch (error) {
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error',
        isPolling: false
      }));
      throw error;
    }
  }, []);

  const checkStatus = useCallback(async (requestId: string) => {
    try {
      const response = await chatAPI.getAsyncStatus(requestId);
      
      setState(prev => ({
        ...prev,
        status: response.status,
        result: response.result?.message || null,
        error: response.error || null,
        isPolling: response.status === 'processing'
      }));

      return response;
    } catch (error) {
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to check status',
        isPolling: false
      }));
      throw error;
    }
  }, []);

  const startPolling = useCallback((requestId: string) => {
    setState(prev => ({
      ...prev,
      requestId,
      status: 'processing',
      isPolling: true,
      error: null,
      result: null
    }));
  }, []);

  const reset = useCallback(() => {
    setState({
      requestId: null,
      status: 'idle',
      result: null,
      error: null,
      isPolling: false
    });
  }, []);

  // Auto-polling when processing
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (state.isPolling && state.requestId) {
      intervalId = setInterval(() => {
        checkStatus(state.requestId!);
      }, 2000); // Check every 2 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [state.isPolling, state.requestId, checkStatus]);

  return {
    ...state,
    startAsyncRequest,
    startPolling,
    checkStatus,
    reset
  };
};
